import { Box, CardContent, Stack, Typography } from "@mui/material";
import { MprcCard, MprcContainer } from "components";
import { ChannelSearchDtoPaginatedList, TopicSearchDtoPaginatedList, TrainingSearchDtoPaginatedList, UserSearchDtoPaginatedList } from "generated/CoreApi_v1";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { FullGlobalSearchTabs } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { SearchRenderShortChannel } from "views/general/GlobalSearch/components/SearchRenderChannel";
import { SearchRenderTopic } from "views/general/GlobalSearch/components/SearchRenderTopic";
import { SearchRenderShortTraining } from "views/general/GlobalSearch/components/SearchRenderTraining";
import { SearchRenderUser } from "views/general/GlobalSearch/components/SearchRenderUser";
import { FullDocSearch } from "views/general/GlobalSearch/FullDocSearch";
import { FullKnowledgeSearch } from "views/general/GlobalSearch/FullKnowledgeSearch";
import { FullTrainingSearch } from "views/general/GlobalSearch/FullTrainingSearch";
import { FullVideoSearch } from "views/general/GlobalSearch/FullVideoSearch";
import { GlobalSearchProvider, useGlobalSearch } from "views/general/GlobalSearch/GlobalSearchProvider";

/////////////////////////////////////////////////////////////////////////////////////////////////

///
const RenderUsers = ({ searchUsers }: { searchUsers: UserSearchDtoPaginatedList; }) => {
  const { t } = useTranslation("shared");

  return (
    <MprcContainer>
      <MprcContainer margin="dense">
        <Typography variant="overline">
          {t("GlobalSearch.FullSearch.PeopleTitle")}
        </Typography>
      </MprcContainer>
      {
        searchUsers.items.map((v, index) => {
          return (
            <SearchRenderUser data={v} />
          );
        })
      }
      {
        searchUsers.totalPages > 1 &&
        <div>
          <Link to={RootPaths.FullUserSearch + document.location.search}>
            <Typography variant="captionHeader" color="info.main">
              {t("GlobalSearch.FullSearch.ShowMore")}
            </Typography>
          </Link>
        </div>
      }

    </MprcContainer>
  );
};

///
const RenderTopics = ({ searchTopics }: { searchTopics: TopicSearchDtoPaginatedList; }) => {
  const { t } = useTranslation("shared");

  return (
    <MprcContainer>

      <MprcContainer margin="dense">
        <Typography variant="overline">
          {t("GlobalSearch.FullSearch.TopicTitle")}
        </Typography>
      </MprcContainer>

      {
        //Se quiser usar divisor: showDivider={index < searchResult.documents.items.length - 1 || searchResult.documents.totalRecords > GlobalSearchFirstPageRowsPerPage}
        searchTopics.items.map((v) => {
          return <SearchRenderTopic key={v.topicId} data={v} />;
        })
      }

      {
        searchTopics.totalPages > 1 &&
        <div>
          <Link to={RootPaths.FullTopicSearch + document.location.search}>
            <Typography variant="captionHeader" color="info.main">
              {t("GlobalSearch.FullSearch.ShowMore")}
            </Typography>
          </Link>
        </div>
      }
    </MprcContainer >
  );
};

///
const RenderChannels = ({ searchChannels }: { searchChannels: ChannelSearchDtoPaginatedList; }) => {
  const { t } = useTranslation("shared");

  return (
    <MprcContainer>

      <MprcContainer margin="dense">
        <Typography variant="overline">
          {t("GlobalSearch.FullSearch.ChannelTitle")}
        </Typography>
      </MprcContainer>

      {
        //Se quiser usar divisor: showDivider={index < searchResult.documents.items.length - 1 || searchResult.documents.totalRecords > GlobalSearchFirstPageRowsPerPage}
        searchChannels.items.map((v) => {
          return <SearchRenderShortChannel
            key={v.channelId}
            data={v}
          />;
        })
      }

      {
        searchChannels.totalPages > 1 &&
        <div>
          <Link to={RootPaths.FullChannelSearch + document.location.search}>
            <Typography variant="captionHeader" color="info.main">
              {t("GlobalSearch.FullSearch.ShowMore")}
            </Typography>
          </Link>
        </div>
      }
    </MprcContainer >
  );
};

///
const RenderTrainings = ({ searchTrainings }: { searchTrainings: TrainingSearchDtoPaginatedList; }) => {
  const { t } = useTranslation("shared");

  return (
    <MprcContainer>

      <MprcContainer margin="dense">
        <Typography variant="overline">
          {t("GlobalSearch.FullSearch.TrainingTitle")}
        </Typography>
      </MprcContainer>

      {
        searchTrainings.items.map((v, index) => {
          return index < 5 &&
            <SearchRenderShortTraining
              key={v.trainingId}
              data={v}
            />;
        })
      }

      {
        searchTrainings.totalRows > 5 &&
        <div>
          <Link to={RootPaths.BuildSearchTab("Trainings")}>
            <Typography variant="captionHeader" color="info.main">
              {t("GlobalSearch.FullSearch.ShowMore")}
            </Typography>
          </Link>
        </div>
      }
    </MprcContainer >
  );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const FullGlobalSearchComponent = () => {

  const navigate = useNavigate();

  const {
    searchResult,
    selectedTab,
    tabOptions,
    pageNumber,
    loading,
    loadingResume,
    isDatasetEmpty,
    anyTabWithData
  } = useGlobalSearch();

  ///
  const renderSummary = () => {
    const showUsers = searchResult!.user.totalRows > 0;
    const showTopics = searchResult!.topic.totalRows > 0;
    const showChannels = searchResult!.channel.totalRows > 0;
    const showTrainings = searchResult!.training.traningSearchResult.totalRows > 0;

    return (showUsers || showTopics || showChannels || showTrainings) &&
      (
        <Box minWidth={290} borderLeft={1} borderColor="muted.light" pl={1}>
          {showUsers && <RenderUsers searchUsers={searchResult!.user} />}
          {showTopics && <RenderTopics searchTopics={searchResult!.topic} />}
          {showChannels && <RenderChannels searchChannels={searchResult!.channel} />}
          {showTrainings && <RenderTrainings searchTrainings={searchResult!.training.traningSearchResult} />}
        </Box>
      );
  };

  ///
  const renderOthers = () => {
    if (searchResult == null) return null;

    const arr: { render: React.ReactNode, path: string; }[] = [];
    if (searchResult.user.totalRows > 0) arr.push({ render: <RenderUsers searchUsers={searchResult!.user} />, path: RootPaths.FullUserSearch });
    if (searchResult.topic.totalRows > 0) arr.push({ render: <RenderTopics searchTopics={searchResult!.topic} />, path: RootPaths.FullTopicSearch });
    if (searchResult.channel.totalRows > 0) arr.push({ render: <RenderChannels searchChannels={searchResult!.channel} />, path: RootPaths.FullChannelSearch });
    if (searchResult.training.traningSearchResult.totalRows > 0) arr.push({ render: <RenderTrainings searchTrainings={searchResult!.training.traningSearchResult} />, path: RootPaths.FullTraining });

    if (arr.length == 1) {
      navigate(arr[0].path + document.location.search);
      return null;
    }

    return (
      <Stack direction="column" width="100%" gap={2}>
        {
          arr.map((item, index) => <MprcCard key={index}><CardContent>{item.render}</CardContent></MprcCard>)
        }
      </Stack>
    );
  };

  ///
  const renderTabs = () => {
    return (
      <MprcCard>
        <FullGlobalSearchTabs
          selectedTab={selectedTab}
          tabOptions={tabOptions}
        />

        <CardContent>
          <Stack direction="row" width="100%">
            <Box flexGrow={1}>
              {
                selectedTab == "All"
                  ? <FullKnowledgeSearch />
                  : selectedTab == "Documents"
                    ? <FullDocSearch />
                    : selectedTab == "Videos"
                      ? <FullVideoSearch />
                      : <FullTrainingSearch />
              }
            </Box>
            {
              pageNumber == 1 && selectedTab == "All" && !loading && searchResult &&
              renderSummary()
            }
          </Stack>
        </CardContent>
      </MprcCard>
    );
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////

  ///
  return (
    <MprcContainer>
      <Box display={loadingResume || isDatasetEmpty ? "none" : ""}>
        {
          anyTabWithData
            ? renderTabs()
            : renderOthers()
        }
      </Box>
    </MprcContainer >
  );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const FullGlobalSearch = () => {
  return (
    <GlobalSearchProvider>
      <FullGlobalSearchComponent />
    </GlobalSearchProvider>
  );
};